import { Box, TableRow, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { Checkbox } from 'common/components/Checkbox';
import { TableCell } from 'common/components/Table';
import { Typography } from 'common/components/Typography';
import { UserAvatar } from 'user/components/UserAvatar';
import { SkillReportMatrixCell } from 'skill/components/SkillReportMatrix';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import {
    IBasicSkillFragment,
    IBasicUserFragment,
    IPortfolioItemMatrixListItemFragment,
} from 'graphql/types';

interface IProps {
    portfolioItems: IPortfolioItemMatrixListItemFragment[];
    skills: IBasicSkillFragment[];
    user: IBasicUserFragment;
    isSelected?: boolean;
    onSelect?(): void;
    onUpdate?(): void;
}

export const SkillReportMatrixRow = ({
    portfolioItems,
    skills,
    user,
    isSelected,
    onSelect,
    onUpdate,
}: IProps) => {
    const theme = useTheme();
    const location = useLocation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();

    const stickyOffsetLeft = {
        xs: 'auto',
        sm: theme.spacing(3),
    };

    const stickyOffset = {
        backgroundColor: 'common.white',
        position: {
            xs: 'static',
            sm: 'sticky',
        },
        left: stickyOffsetLeft,
    };

    return (
        <TableRow key={user.id}>
            <TableCell>
                <Checkbox
                    checked={isSelected}
                    onClick={(e) => {
                        onSelect?.();

                        e.stopPropagation();
                    }}
                />
            </TableCell>

            <TableCell
                colSpan={0}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                    openGlobalDrawer({
                        type: IGlobalDrawerType.UserProfile,
                        itemId: user.id,
                        props: {
                            onUpdate: () => {
                                onUpdate?.();
                            },
                            onArchive: () => {
                                onUpdate?.();

                                closeGlobalDrawer({
                                    type: IGlobalDrawerType.UserProfile,
                                });
                            },
                        },
                    });
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <UserAvatar bgColor="secondary" size={45} user={user} />

                    <Typography sx={{ ml: 2 }} variant="body2">
                        {user.name}
                    </Typography>
                </Box>
            </TableCell>

            {skills.map((skill) => {
                const portfolioItem = portfolioItems.find(
                    (item) => skill.id === item.relatedId
                );

                return (
                    <SkillReportMatrixCell
                        align="center"
                        key={skill.id}
                        portfolioItem={portfolioItem}
                        skill={skill}
                        sx={stickyOffset}
                        userId={user.id}
                        userName={user.name}
                        onUpdate={(portfolioItemId) => {
                            onUpdate?.();

                            if (portfolioItemId) {
                                openGlobalDrawer(
                                    {
                                        type: IGlobalDrawerType.PortfolioItem,
                                        itemId: portfolioItemId,
                                    },
                                    location
                                );
                            }
                        }}
                    />
                );
            })}
        </TableRow>
    );
};
